import * as L from 'leaflet';
import 'leaflet.markercluster';
import { markerClusterGroup } from 'leaflet';
import {
  AfterViewInit,
  Component,
  effect,
  inject,
  input,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MapOptionsService } from './services/map-options.service';
import { MapDataService } from './services/map-data.service';
import { MapButtonGroupComponent } from './components/map-button-group/map-button-group.component';
import { LegendPanelComponent } from './components/legend-panel/legend-panel.component';
import { LayerBuilderService } from './services/layer-builder.service';
import { LocationService } from '@core/services/location.service';
import {
  catchError,
  debounceTime,
  filter,
  fromEvent,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { LanguageDialogService } from '@shared/components/language-dialog/language-dialog.service';
import {
  ExtraLayerData,
  MapLayer,
  RouteHighlightRenderers,
  RouteRenderers,
} from './interfaces/map';
import {
  RouteEdgePane,
  RouteHighlightPanes,
  WalkingSegmentMarkerPane,
} from './constants/constants';
import {
  GetRouteNetworksInAreaReq,
  GetRouteNetworksInAreaRes,
  POIInAreaReq,
  POIInAreaResp,
  RouteNetwork,
  RouteNetworkMaintainer,
} from './interfaces/map-data';
import { LocationAccessBlockedDialogService } from '@shared/components/location-access-blocked-dialog/location-access.blocked-dialog.service';
import { MapLayerProcessingService } from './services/map-layer-processing.service';
import { ServerResponse } from './constants/server-response.enum';
import { DebugModeService } from '@shared/services/debug-mode.service';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import MapUtils from './utils/map-utils';
import { RouteNetworkSearchComponent } from './components/route-network-search/route-network-search.component';
import { ActivatedRoute, Router } from '@angular/router';
import { ClipboardService } from './services/clipboard.service';
import { AppDownloadBannerComponent } from '@shared/components/app-download-banner/app-download-banner.component';
import { RoutePlannerService } from './services/route-planner.service';
import { MapLayerCacheService } from './services/map-layer-cache.service';
import { MapMovementService } from './services/map-movement.service';
import { MapStateService } from './services/map-state-service';
import MapZoomRulesUtils from './utils/map-zoom-rules.utils';
import { RouteDirectionsRes } from './interfaces/route-planning-api';
import { ElevationProfileComponent } from './components/elevation-profile/elevation-profile.component';
import { PoiVisibilityService } from './services/poi-visibility.service';
import { RouteHighlightService } from './services/route-highlight.service';
import { createRouteLineCanvas } from './utils/route-line-canvas';

@Component({
  selector: 'app-map',
  imports: [
    MapButtonGroupComponent,
    LegendPanelComponent,
    RouteNetworkSearchComponent,
    AppDownloadBannerComponent,
    ElevationProfileComponent,
  ],
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss', './styles/leaflet-overrides.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [MapOptionsService, MapLayerCacheService, MapMovementService, MapStateService],
})
export class MapComponent implements AfterViewInit {
  private optionsService = inject(MapOptionsService);
  private mapDataService = inject(MapDataService);
  private layerBuilderService = inject(LayerBuilderService);
  private locationService = inject(LocationService);
  private languageDialog = inject(LanguageDialogService);
  private poiVisibilityService = inject(PoiVisibilityService);
  private locationBlockedDialogService = inject(LocationAccessBlockedDialogService);
  private layerProcessingService = inject(MapLayerProcessingService);
  private devModeService = inject(DebugModeService);
  private clipboardService = inject(ClipboardService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private routePlannerService = inject(RoutePlannerService);
  private layerCacheService = inject(MapLayerCacheService);
  private movementService = inject(MapMovementService);
  private mapStateService = inject(MapStateService);
  private routeHighlightService = inject(RouteHighlightService);

  sideSheetUrl = input<string | undefined | null>();

  mapComponent!: L.Map;
  readonly defaultMapCenter: [number, number] = [63.4305, 10.3951];
  mapId = signal<string>('viaTracksRouteMap').asReadonly();
  mapBounds = signal<
    | Pick<GetRouteNetworksInAreaReq & POIInAreaReq, 'MaxLat' | 'MaxLon' | 'MinLat' | 'MinLon'>
    | undefined
  >(undefined);
  selectedRouteNetworkData = signal<ExtraLayerData | undefined>(undefined);
  lastRouteData = signal<RouteDirectionsRes | undefined>(undefined);
  isLegendOpen = signal(false);

  private userLocationMarkerLayer: MapLayer | undefined;
  private routeNetworkClusterGroup: L.MarkerClusterGroup = markerClusterGroup(
    this.layerBuilderService.getRouteMarkerClusterGroupOptions(),
  );
  private isFirstRouteNetworkCall = true;
  private canvasRenderer: L.Renderer | undefined = undefined;
  private routeEdgeRenderer: L.Renderer | undefined = undefined;
  private walkingSegmentMarkersFrame: number | undefined;
  private routeHighlightRenderers: RouteHighlightRenderers = {};
  private routeHighlightLayers: MapLayer[] = [];
  private showRouteHighlightPending = false;
  /** set by a map click: the snowflake highlights the whole route, a line only its segment */
  private clickedRoute?: { routeNetworkId: number; routeSegmentId?: number };

  private poiTrigger = signal(0);
  private routeNetworkMaintainers = toSignal(
    this.mapDataService
      .getRouteMaintainers()
      .pipe(map((resp) => resp?.RouteNetworkMaintainers || [])),
  );
  private routeNetworkMaintainersUpdate = signal<RouteNetworkMaintainer[]>([]);
  private routeNetworksUpdate = signal<RouteNetwork[]>([]);

  routeNetworksResource = rxResource({
    params: () => {
      const mapBounds = this.mapBounds();
      if (!mapBounds) return null;
      return { mapBounds };
    },

    stream: ({ params, abortSignal }) => {
      if (!params) return of([]);

      const abort$ = fromEvent(abortSignal, 'abort');
      return of(params).pipe(
        debounceTime(500),
        switchMap(({ mapBounds }) =>
          this.mapDataService.getRouteNetworksInArea(
            {
              ...mapBounds,
              IncludeSegments: MapZoomRulesUtils.includeRouteSegments(this.mapComponent.getZoom()),
            },
            abortSignal,
          ),
        ),
        tap(() => {
          if (this.isFirstRouteNetworkCall) {
            this.subscribeToRoutNetworkUpdates();
            this.isFirstRouteNetworkCall = false;
          }
        }),
        takeUntil(abort$),
        catchError(() => of([])),
      );
    },
  });

  poiInAreaResource = rxResource({
    params: () => {
      const mapBounds = this.mapBounds();
      const trigger = this.poiTrigger();

      if (!mapBounds) return null;
      return { mapBounds, trigger };
    },

    stream: ({ params, abortSignal }) => {
      if (!params) return of([]);

      if (this.mapComponent.getZoom() < 13) {
        return of(null);
      }

      const abort$ = fromEvent(abortSignal, 'abort');
      this.layerProcessingService.removeApiOriginLayersFromMap(
        this.layerCacheService.getPOICache(),
        this.mapComponent,
      );
      this.layerCacheService.updatePOICache([]);
      return of(params).pipe(
        debounceTime(500),
        switchMap(({ mapBounds }) => this.mapDataService.getPOIInArea(mapBounds, abortSignal)),
        takeUntil(abort$),
        catchError(() => of([])),
      );
    },
  });

  routeDirectionsResource = rxResource({
    params: () => {
      if (!this.routePlannerService.canFetchRoute()) return null;
      return { waypoints: this.routePlannerService.waypoints() };
    },
    stream: ({ params }) => {
      if (!params) return of(null);

      return this.routePlannerService.getDirections(this.mapComponent.getZoom());
    },
  });

  constructor() {
    effect(() => {
      const maintainersUpdate = this.routeNetworkMaintainersUpdate();
      if (maintainersUpdate?.length) {
        this.updateExistingMaintainerLayers(maintainersUpdate);
      }
    });

    effect(() => {
      const sideSheetOpen = this.sideSheetUrl();

      if (!sideSheetOpen) {
        this.layerBuilderService.stopPulse();
      }

      untracked(() => {
        this.routeHighlightService.clear();
        if (this.clickedRoute?.routeNetworkId !== Number(sideSheetOpen)) {
          this.clickedRoute = undefined;
        }
        this.showRouteHighlightPending =
          !!this.clickedRoute && this.clickedRoute.routeSegmentId === undefined;
        this.refreshRouteHighlight();
      });
    });

    effect(() => {
      const sectionFilter = this.routeHighlightService.sectionFilter();

      untracked(() => {
        if (sectionFilter) this.showRouteHighlightPending = true;
        this.refreshRouteHighlight();
      });
    });

    this.setupRouteNetworkEffects();
    this.setupRouteDirectionsEffects();
    this.setupMaintainersEffects();
    this.setupPoiEffects();

    this.subscribeToRouteMaintainerUpdates();
  }

  ngAfterViewInit(): void {
    this.readUrlAndSetCoordinates();
    this.initLayers();
    this.setupMapEvents();
    this.optionsService.addLogoControl(this.mapComponent);
  }

  onElevationProfileClosed(): void {
    this.lastRouteData.set(undefined);
    this.routePlannerService.clearWaypoints(this.mapComponent);
  }

  toggleLegend(): void {
    this.isLegendOpen.update((isOpen) => !isOpen);
  }

  focusMyLocation(): void {
    this.getAndNavigateToUserLocation(true);
  }

  changeLanguage(): void {
    this.languageDialog.openLanguageDialog();
  }

  mapViewToggled(isArealViewActive: boolean): void {
    this.optionsService.switchMapView(this.mapComponent, isArealViewActive);
  }

  focusMapToLatLng(network: RouteNetwork | undefined): void {
    if (!network || !network.RouteNetworkID) return;
    this.mapComponent.setView([network?.Latitude!, network?.Longitude!], 13, { animate: true });
    this.routeHighlightService.clear();
    this.clickedRoute = undefined;
    this.refreshRouteHighlight();
    this.navigateToRouteNetwork(network);
  }

  private setupRouteDirectionsEffects(): void {
    effect(() => {
      if (this.routeDirectionsResource.error()) return;

      const route = this.routeDirectionsResource.value() as RouteDirectionsRes | undefined;
      if (!route?.Segments?.length) return;
      this.routePlannerService.drawRoute(route, this.mapComponent);
    });

    effect(() => {
      const value = this.routeDirectionsResource.value() as RouteDirectionsRes | undefined;
      if (value?.Segments?.length) {
        this.lastRouteData.set(value);
      }
    });
    effect(() => {
      if (!this.routePlannerService.hasEnoughWaypoints()) {
        this.lastRouteData.set(undefined);
      }
    });
  }

  private setupMaintainersEffects(): void {
    effect(() => {
      const routeNetworkMaintainers = this.routeNetworkMaintainers();
      if (routeNetworkMaintainers?.length) {
        const layers = this.layerBuilderService.buildRouteMaintainerLayers(
          routeNetworkMaintainers || [],
        );
        if (!((this.mapComponent?.getZoom() || 0) < 10)) {
          layers.forEach((layer) => {
            this.mapComponent.addLayer(layer);
          });
        }
        this.layerCacheService.updateMaintainersCache(layers);
      }
    });
  }

  private setupPoiEffects(): void {
    effect(() => {
      const poiInArea = this.poiInAreaResource.value() as POIInAreaResp | undefined;
      if (poiInArea?.POIs?.length) {
        this.mapComponent.eachLayer((layer) => {
          this.layerProcessingService.removeApiOriginLayersFromMap(
            [layer],
            this.mapComponent,
            (layer) => !!(layer instanceof L.Marker && (layer as MapLayer).extraData?.isPoiMarker),
          );
        });
        const layers = this.layerBuilderService.buildPOILayers(poiInArea.POIs);
        this.layerCacheService.updatePOICache(layers);
        untracked(() => {
          this.poiVisibilityService.addMetTypes(poiInArea.POIs!);
          this.showPoiLayersByType();
        });
        this.addAndRemoveTooltips();
      } else {
        this.layerProcessingService.removeApiOriginLayersFromMap(
          this.layerCacheService.getPOICache(),
          this.mapComponent,
        );
        this.layerCacheService.updatePOICache([]);
      }
    });

    effect(() => {
      this.poiVisibilityService.hiddenKeys();
      untracked(() => {
        if (this.mapComponent && !MapZoomRulesUtils.hidePOI(this.mapComponent.getZoom())) {
          this.showPoiLayersByType();
        }
      });
    });
  }

  private showPoiLayersByType(): void {
    const hiddenKeys = this.poiVisibilityService.hiddenKeys();
    this.layerCacheService.getPOICache().forEach((layer) => {
      const typeKey = layer.extraData?.poiTypeKey;
      if (typeKey && hiddenKeys.has(typeKey)) {
        this.mapComponent.removeLayer(layer);
      } else {
        this.mapComponent.addLayer(layer);
      }
    });
  }

  private setupRouteNetworkEffects(): void {
    effect(() => {
      const routeNetworksResp = this.routeNetworksResource.value() as
        GetRouteNetworksInAreaRes | undefined;

      untracked(() => {
        this.optionsService.checkAndBuildZoomNeededControl(
          this.mapComponent,
          routeNetworksResp?.ServerResponse || ServerResponse.RouteNetworkExceeded,
        );

        if (routeNetworksResp?.RouteNetworks) {
          // clear route network markers and segments
          this.layerProcessingService.removeRouteNetworkLayersFromMap(this.mapComponent);

          // clear previous route network marker cluster layers
          this.routeNetworkClusterGroup.clearLayers();

          // build and add newly fetched layers
          const layers = this.layerBuilderService.buildRouteNetworkLayers(
            routeNetworksResp.RouteNetworks,
            this.routeRenderers(),
            this.navigateToRouteNetworkFn,
          );

          this.addLayersToMarkerClusterGroup(layers.networkLayers);
          this.addLayersToMap(layers.polylines);
          this.routePlannerService.attachAddStopEventHandlerToPolylinePopups(
            layers.polylines,
            this.mapComponent,
          );
          this.refreshRouteHighlight();

          this.mapDataService.setBoundsForRouteNetworkUpdates({
            ...this.mapBounds(),
            IncludeSegments: MapZoomRulesUtils.includeRouteSegments(this.mapComponent.getZoom()),
          } as GetRouteNetworksInAreaReq);
        }
      });
    });

    effect(() => {
      const routeNetworksUpdate = this.routeNetworksUpdate();
      if (routeNetworksUpdate?.length) {
        // redraw only updated route markers
        const networkMarkersInMap = this.routeNetworkClusterGroup.getLayers();
        const routeNetworkMarkersToRemove = networkMarkersInMap.filter((layer: MapLayer) => {
          return !!routeNetworksUpdate.find(
            (updatedNetwork) => layer.extraData?.routeNetworkId === updatedNetwork.RouteNetworkID,
          );
        });
        this.routeNetworkClusterGroup.removeLayers(routeNetworkMarkersToRemove);

        // adding (updating after removal) only the networks (markers) that currently exist on the map
        const markersToUpdate = routeNetworksUpdate.filter((network) =>
          networkMarkersInMap.find(
            (marker: MapLayer) => marker.extraData?.routeNetworkId === network.RouteNetworkID,
          ),
        );
        const newLayersToAdd = this.layerBuilderService.buildRouteNetworkLayers(
          markersToUpdate,
          this.routeRenderers(),
          this.navigateToRouteNetworkFn,
        );
        this.addLayersToMarkerClusterGroup(newLayersToAdd.networkLayers);

        // updating only those routeNetwork segments that are in update
        const allMapPolylines: MapLayer[] = this.getAllMapLayers().filter(
          (layer) => layer instanceof L.Polyline || layer.extraData?.isWalkingSegmentMarker,
        );
        const segmentsToRedraw =
          this.layerProcessingService.findAndUpdateChangedPolylineLayerColors(
            routeNetworksUpdate,
            allMapPolylines,
          );
        segmentsToRedraw.forEach(({ network, segment, layersToRemove }) => {
          layersToRemove.forEach((layer) => this.mapComponent.removeLayer(layer));

          const polylines = this.layerBuilderService.buildRouteSegments(
            { ...network, RouteSegments: [segment] },
            this.routeRenderers(),
            this.navigateToRouteNetworkFn,
          );
          this.addLayersToMap(polylines);
          this.routePlannerService.attachAddStopEventHandlerToPolylinePopups(
            polylines,
            this.mapComponent,
          );
        });

        untracked(() => this.refreshRouteHighlight());
      }
    });
  }

  private refreshRouteHighlight(): void {
    if (!this.mapComponent) return;

    this.routeHighlightLayers.forEach((layer) => this.mapComponent.removeLayer(layer));
    this.routeHighlightLayers = [];

    const routeNetworkId = Number(this.sideSheetUrl());
    if (!routeNetworkId) return;

    const sectionFilter = this.routeHighlightService.sectionFilter();
    if (!sectionFilter && !this.clickedRoute) return;
    const clickedSegmentId = sectionFilter ? undefined : this.clickedRoute?.routeSegmentId;
    const polylines = this.getAllMapLayers().filter(
      (layer): layer is L.Polyline & MapLayer =>
        layer instanceof L.Polyline &&
        (layer as MapLayer).extraData?.routeNetworkId === routeNetworkId &&
        !(layer as MapLayer).extraData?.isRouteEdge &&
        (clickedSegmentId === undefined ||
          (layer as MapLayer).extraData?.routeSegmentId === clickedSegmentId) &&
        RouteHighlightService.matches((layer as MapLayer).extraData, sectionFilter),
    );
    if (!polylines.length) return;

    this.routeHighlightLayers = this.layerBuilderService.buildRouteHighlightLayers(
      polylines,
      this.routeHighlightRenderers,
    );
    this.addLayersToMap(this.routeHighlightLayers);

    if (this.showRouteHighlightPending) {
      this.showRouteHighlightPending = false;
      MapUtils.showBoundsBesideRouteSheet(
        this.mapComponent,
        L.latLngBounds(polylines.flatMap((polyline) => polyline.getLatLngs() as L.LatLng[])),
      );
    }
  }

  private initLayers(): void {
    this.optionsService.addBaseControls(this.mapComponent);
  }

  private updateExistingMaintainerLayers(maintainers: RouteNetworkMaintainer[]): void {
    const updatedMaintainerLayers = this.layerProcessingService.processUpdatedMaintainerLayers(
      this.layerCacheService.getMaintainersCache(),
      maintainers,
      this.mapComponent,
      MapZoomRulesUtils.hideMaintainers(this.mapComponent.getZoom()),
    );
    this.layerCacheService.updateMaintainersCache(updatedMaintainerLayers);
  }

  private setupMapEvents(): void {
    fromEvent(this.mapComponent, 'moveend')
      .pipe(debounceTime(100))
      .subscribe(() => {
        if (this.movementService.zoomChanged(this.mapComponent)) {
          this.toggleMaintainersOnZoom();
          this.togglePOISOnZoom();
          this.mapStateService.saveLastPosition(
            this.mapComponent.getCenter(),
            this.mapComponent.getZoom(),
          );
        }
        this.checkMovement();
      });

    this.clipboardService.setupMapDblClickEvent(this.mapComponent);

    // walking segment markers depend on the zoom and on where all other markers are
    this.mapComponent.on('zoomend layeradd layerremove', this.scheduleWalkingSegmentMarkersUpdate);
    this.routeNetworkClusterGroup.on('animationend', this.scheduleWalkingSegmentMarkersUpdate);
  }

  private toggleMaintainersOnZoom(): void {
    if (MapZoomRulesUtils.hideMaintainers(this.mapComponent.getZoom())) {
      this.layerProcessingService.removeApiOriginLayersFromMap(
        this.layerCacheService.getMaintainersCache(),
        this.mapComponent,
      );
    } else {
      this.layerCacheService.getMaintainersCache().forEach((l) => this.mapComponent.addLayer(l));
    }
  }

  private togglePOISOnZoom(): void {
    if (MapZoomRulesUtils.hidePOI(this.mapComponent.getZoom())) {
      this.layerProcessingService.removeApiOriginLayersFromMap(
        this.layerCacheService.getPOICache(),
        this.mapComponent,
      );
      return;
    }

    if (!this.layerCacheService.hasPOI()) {
      this.poiTrigger.update((v) => v + 1);
      return;
    }

    this.showPoiLayersByType();
  }

  private addLayersToMap(layers: MapLayer[]): void {
    if (!layers.length) return;

    layers.forEach((layer) => {
      this.mapComponent.addLayer(layer);
    });
  }

  private findPulsingMarker(layers: MapLayer[]): void {
    const routeNetworkIdFromUrl = this.sideSheetUrl();

    const pulsingMarker = layers.find(
      (l) =>
        l instanceof L.Marker &&
        routeNetworkIdFromUrl &&
        Number(routeNetworkIdFromUrl) === (l as MapLayer).extraData?.routeNetworkId,
    );

    if (pulsingMarker) {
      this.layerBuilderService.startPulse(pulsingMarker as L.Marker);
    }
  }

  private addLayersToMarkerClusterGroup(layers: MapLayer[]): void {
    this.routeNetworkClusterGroup.addLayers(layers);
    // in case route network cluster does not exist add it again to the map
    if (!this.mapComponent.hasLayer(this.routeNetworkClusterGroup)) {
      this.mapComponent.addLayer(this.routeNetworkClusterGroup);
    }
    this.findPulsingMarker(this.routeNetworkClusterGroup.getLayers());
    this.addAndRemoveTooltips();
  }

  private checkMovement(): void {
    const hasPolylines = this.hasPolylines();
    const shouldUpdate = this.movementService.shouldUpdate(this.mapComponent, hasPolylines);

    if (shouldUpdate) {
      this.setMapBoundsSignalValue();
      this.mapStateService.saveLastPosition(
        this.mapComponent.getCenter(),
        this.mapComponent.getZoom(),
      );
    } else {
      this.addAndRemoveTooltips();
    }
  }

  private triggerBoundsUpdate(center: L.LatLng, zoom: number, bounds: L.LatLngBounds): void {
    this.setMapBoundsSignalValue();
    this.movementService.updateState(center, zoom, bounds);
    this.mapStateService.saveLastPosition(center, zoom);
  }

  private hasPolylines(): boolean {
    const layers = this.getAllMapLayers();
    for (const id in layers) {
      if (layers[id] instanceof L.Polyline) {
        return true;
      }
    }
    return false;
  }

  private addAndRemoveTooltips(): void {
    this.layerProcessingService.addOrRemoveNetworkTooltips(
      this.mapComponent,
      this.routeNetworkClusterGroup,
    );

    if (this.devModeService.devModeEnabled$.getValue()) {
      this.layerProcessingService.addOrRemoveSegmentLineTooltips(this.mapComponent);
    }
  }

  private focusMapOnUserLocation(lat: number, lng: number) {
    if (this.userLocationMarkerLayer) {
      this.layerProcessingService.removeApiOriginLayersFromMap(
        [this.userLocationMarkerLayer],
        this.mapComponent,
      );
    }
    this.mapComponent.setView([lat, lng], 14, { animate: true });

    const marker = this.layerBuilderService.getUserLocationIconLayer(lat, lng);

    marker.addTo(this.mapComponent);
    requestAnimationFrame(() => {
      this.triggerBoundsUpdate(
        this.mapComponent.getCenter(),
        this.mapComponent.getZoom(),
        this.mapComponent.getBounds(),
      );
    });

    this.userLocationMarkerLayer = marker;
  }

  private getAndNavigateToUserLocation(isClickEvent = false): void {
    this.locationService.getUserLocation$().subscribe({
      next: (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (lat && lng) {
          this.focusMapOnUserLocation(lat, lng);
        }
      },
      error: (err) => {
        if (err.error === 'blocked' && isClickEvent) {
          this.locationBlockedDialogService.openLocationAccessBlockedDialog();
        } else {
          this.triggerBoundsUpdate(
            this.mapComponent.getCenter(),
            this.mapComponent.getZoom(),
            this.mapComponent.getBounds(),
          );
        }
      },
    });
  }

  private setMapBoundsSignalValue(): void {
    const mapBounds = MapUtils.getMapBoundsForNetworks(this.mapComponent);
    this.mapBounds.set(mapBounds);
  }

  /** runs at most once per frame, many layers are added at once when route networks load */
  private scheduleWalkingSegmentMarkersUpdate = (): void => {
    if (this.walkingSegmentMarkersFrame !== undefined) return;

    this.walkingSegmentMarkersFrame = requestAnimationFrame(() => {
      this.walkingSegmentMarkersFrame = undefined;
      this.layerProcessingService.updateWalkingSegmentMarkers(this.mapComponent);
    });
  };

  private routeRenderers(): RouteRenderers {
    return { line: this.canvasRenderer, edge: this.routeEdgeRenderer };
  }

  private getAllMapLayers(): MapLayer[] {
    return Object.values((this.mapComponent as any)._layers) || [];
  }

  private navigateToRouteNetworkFn = (
    routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
    routeSegmentId?: number,
  ): void => {
    this.routeHighlightService.clear();
    this.clickedRoute = { routeNetworkId: routeNetwork.RouteNetworkID!, routeSegmentId };
    if (Number(this.sideSheetUrl()) === routeNetwork.RouteNetworkID) {
      this.showRouteHighlightPending = routeSegmentId === undefined;
      this.refreshRouteHighlight();
    }
    this.navigateToRouteNetwork(routeNetwork);
  };

  private navigateToRouteNetwork(
    routeNetwork: Pick<RouteNetwork, 'RouteNetworkID' | 'Latitude' | 'Longitude'>,
  ): void {
    this.router.navigate(['./', routeNetwork.RouteNetworkID!], {
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      queryParams: {
        lat: routeNetwork.Latitude?.toFixed(5),
        lng: routeNetwork.Longitude?.toFixed(5),
      },
    });
  }

  private readUrlAndSetCoordinates(): void {
    this.mapComponent = this.mapStateService.initMap(this.mapId());
    this.canvasRenderer = createRouteLineCanvas(
      { tolerance: 1 },
      () => Number(this.sideSheetUrl()) || undefined,
    );

    const routeEdgePane = this.mapComponent.createPane(RouteEdgePane);
    routeEdgePane.style.zIndex = '399';
    routeEdgePane.style.pointerEvents = 'none';
    this.routeEdgeRenderer = L.canvas({ pane: RouteEdgePane });

    const walkingSegmentMarkerPane = this.mapComponent.createPane(WalkingSegmentMarkerPane);
    walkingSegmentMarkerPane.style.zIndex = '450';
    walkingSegmentMarkerPane.style.pointerEvents = 'none';

    Object.entries(RouteHighlightPanes).forEach(([part, paneName], index) => {
      const pane = this.mapComponent.createPane(paneName);
      pane.style.zIndex = String(401 + index);
      pane.style.pointerEvents = 'none';
      if (part === 'halo') pane.style.opacity = '0.35';
      this.routeHighlightRenderers[part as keyof RouteHighlightRenderers] = L.canvas({
        pane: paneName,
      });
    });

    if (this.mapStateService.isFirstLoad()) {
      this.getAndNavigateToUserLocation();
    } else {
      this.setMapBoundsSignalValue();
    }
  }

  private subscribeToRouteMaintainerUpdates(): void {
    this.mapDataService
      .getRouteMaintainerUpdates()
      .pipe(filter(() => !!this.layerCacheService.hasMaintainers()))
      .subscribe((resp) => this.routeNetworkMaintainersUpdate.set(resp));
  }

  /** subscription is alive through whole app's lifecycle */
  private subscribeToRoutNetworkUpdates(): void {
    this.mapDataService
      .getRouteNetworkUpdates()
      .subscribe((resp) => this.routeNetworksUpdate.set(resp));
  }
}
